import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { revokPortalDocument, sendDocumentToPortal } from "@/Documents/Api/PortalApi";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { BaseSendToPortalPopupStrategy } from "@/Portal/Services/Strategies/BaseSendToPortalPopupStrategy";

class SendDocumentStrategy extends BaseSendToPortalPopupStrategy {
    public readonly disclaimer = "";
    public readonly itemCheckOperation: number = LocalOperations.Documents.PortalDocumentNoteMessageChecked;

    constructor() {
        super(
            PortalLinkedItemType.Document,
            LocalOperations.Documents.PortalRevokedClicked,
            LocalOperations.Documents.PortalStatusCancel
        );
    }

    public onSendToPortalClick(
        subject: string,
        newMessage: string,
        includeDocument: boolean,
        portalItemModel?: IPortalLinkedItemModel,
        priority?: SharedItemPriority,
        canPatientReplyWithMessage?: boolean
    ): void {
        sessionStore.dispatch(
            LocalOperations.Documents.PortalSendClicked,
            {
                subject: subject,
                messageText: newMessage,
                includeDocument: includeDocument,
                portalItemModel: portalItemModel,
                priority: priority,
                canPatientReplyWithMessage: canPatientReplyWithMessage
            }
        );
    }

    public async onSendToPortalClickAsync(
        subject: string,
        newMessage: string,
        includeDocument: boolean,
        portalItemModel?: IPortalLinkedItemModel,
        priority?: SharedItemPriority,
        canPatientReplyWithMessage?: boolean
    ): Promise<boolean> {
        const objectId: string = !IsNullOrWhiteSpace(portalItemModel.get("LinkedItemId")) ? portalItemModel.get("LinkedItemId") : "";
        const sharedItemId = portalItemModel.get("LinkedItemModel").getSharedItemId();
        const patientId = (portalItemModel.get("LinkedItemModel") as MedicalDocumentItem).Model.Patients[0].Id;
        let portalMessages: SharedItemMessage[] = [];

        if (portalItemModel) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(portalItemModel);
        }

        if (!IsNullOrWhiteSpace(newMessage)) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(newMessage, priority));
        }

        const sharedItem = new SharedItem({
            SharedItemId: sharedItemId,
            Subject: subject,
            SharedItemType: PortalLinkedItemType.Document,
            PatientContactId: patientId,
            Messages: portalMessages,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        });

        const portalResponse = await sendDocumentToPortal(
            objectId,
            sharedItem,
            includeDocument
        );

        return portalResponse.isSuccess;
    }

    public async onRevokeClickAsync(portalItemModel: IPortalLinkedItemModel): Promise<boolean> {
        let messageIdsToRevoke: number[] = [];
        const objectId: string = portalItemModel.get("LinkedItemId") ?? "";

        if (portalItemModel.get("LinkedItemMessages")) {
            const allPortalMessages = portalItemModel.get("LinkedItemMessages");

            // revoke only pending messages
            allPortalMessages.map(x => {
                if (x.Status === PortalSyncStatus.Pending ||
                    x.Status === PortalSyncStatus.Viewed ||
                    x.Status === PortalSyncStatus.Error ||
                    x.Status === PortalSyncStatus.Unknown) {
                    messageIdsToRevoke.push(+x.SharedItemMessageId);
                }
            });
        }

        const sharedItemId: string = portalItemModel.get("LinkedItemModel").getSharedItemId();
        const revokeResponse = await revokPortalDocument(
            sharedItemId,
            messageIdsToRevoke,
            sessionStore.userId,
            PortalLinkedItemType.Document,
            objectId,
            true
        );

        return revokeResponse.isSuccess;
    }

    public onPortalSharedItemMessageCheckChange(portalItemModel: IPortalLinkedItemModel, message: SharedItemMessage): Immutable.List<SharedItemMessage> {
        const model = PatientHelper.handlePortalItemCheck(portalItemModel, message);
        return model.get("LinkedItemMessages");
    }
}

export { SendDocumentStrategy };
